import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  LayoutList,
  Link2,
  ShieldCheck,
  Trash2,
  Upload,
  BarChart3,
} from 'lucide-react';

const WEBSITE_SIGNUP_URL =
  process.env.REACT_APP_WEBSITE_SIGNUP_URL || 'https://www.slpmicrosystems.com/signup';

const STEPS = [
  {
    n: '01',
    title: 'Bring the punches',
    body: 'Export from a clock, NICE, Accutime, or any other system that can produce a punch file.',
  },
  {
    n: '02',
    title: 'Pair and map',
    body: 'TideSync pairs In/Out, carries leftover punches across files, and resolves badge IDs to Fusion person numbers.',
  },
  {
    n: '03',
    title: 'Preview before pay',
    body: 'Review the segments that will land in Oracle. Exceptions stay visible — they do not disappear into Fusion.',
  },
  {
    n: '04',
    title: 'Load into OTL',
    body: 'One post to timeRecordEventRequests. Jobs, logs, and error files stay in TideSync for payroll close.',
  },
];

const PREBUILTS = [
  {
    name: 'NICE',
    badge: 'Prebuilt',
    title: 'EventTime + EventState',
    points: [
      'LoggedIn / LoggedOut pair into work segments',
      'LunchOut / LunchIn map to meal-break time types',
      'HD_NICE_ID → Person Number lookup',
    ],
  },
  {
    name: 'Accutime',
    badge: 'Prebuilt',
    title: 'Start/Stop or punch rows',
    points: [
      'Date + StartTime + StopTime, or Timestamp Wall Time + In/Out',
      'Midnight-crossing shifts stay one pair',
      'Unpaired Ins carry forward to the next file',
    ],
  },
  {
    name: 'Other systems',
    badge: 'Same load',
    title: 'Any clock or software export',
    points: [
      'Bring punches from a clock or another WFM as a file',
      'NICE and Accutime layouts load without a custom project',
      'Same preview, job log, and OTL post',
    ],
  },
];

const PUNCH_TAPE = [
  ['IN', '06:58', 'Badge 4412'],
  ['OUT', '15:31', 'Person 1008'],
  ['IN', '07:04', 'NICE LoggedIn'],
  ['OUT', '12:02', 'LunchOut'],
  ['IN', '12:34', 'LunchIn'],
  ['IN', '06:41', 'Accutime'],
  ['OUT', '16:12', 'Badge 2209'],
  ['IN', '07:11', 'Person 884'],
];

function PunchScene() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMax slice">
        <defs>
          <linearGradient id="landing-floor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0f766e" stopOpacity="0" />
            <stop offset="42%" stopColor="#115e59" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#082f49" stopOpacity="0.88" />
          </linearGradient>
          <linearGradient id="landing-window" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fdba74" stopOpacity="0.62" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.14" />
          </linearGradient>
          <linearGradient id="landing-screen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5eead4" />
            <stop offset="100%" stopColor="#0f766e" />
          </linearGradient>
        </defs>
        <rect width="1440" height="900" fill="url(#landing-floor)" />
        <rect x="940" y="48" width="250" height="300" rx="10" fill="url(#landing-window)" opacity="0.5" />
        <rect x="1210" y="48" width="190" height="300" rx="10" fill="url(#landing-window)" opacity="0.32" />
        <line x1="940" y1="348" x2="1400" y2="348" stroke="#082f49" strokeWidth="10" opacity="0.35" />
        <ellipse cx="1160" cy="828" rx="560" ry="74" fill="#082f49" opacity="0.5" />

        {/* Wall clock */}
        <g transform="translate(1248 88)" fill="none" stroke="#ccfbf1" strokeWidth="3" opacity="0.7">
          <circle cx="70" cy="70" r="66" />
          <circle cx="70" cy="70" r="5" fill="#ccfbf1" />
          <line x1="70" y1="70" x2="70" y2="26" />
          <line x1="70" y1="70" x2="112" y2="70" />
        </g>

        {/* Time-clock kiosk */}
        <g>
          <rect x="1096" y="428" width="108" height="268" rx="10" fill="#134e4a" />
          <rect x="1108" y="348" width="84" height="92" rx="8" fill="#0f172a" />
          <rect x="1116" y="356" width="68" height="52" rx="5" fill="url(#landing-screen)" />
          <text x="1150" y="378" textAnchor="middle" fill="#042f2e" fontFamily="ui-sans-serif,system-ui" fontSize="13" fontWeight="700">
            IN
          </text>
          <text x="1150" y="398" textAnchor="middle" fill="#042f2e" fontFamily="ui-monospace,monospace" fontSize="14" fontWeight="700">
            06:58
          </text>
          <rect x="1128" y="416" width="44" height="8" rx="2" fill="#5eead4" opacity="0.7" />
          <g fill="#99f6e4" opacity="0.45">
            <circle cx="1128" cy="456" r="4" />
            <circle cx="1146" cy="456" r="4" />
            <circle cx="1164" cy="456" r="4" />
            <circle cx="1128" cy="474" r="4" />
            <circle cx="1146" cy="474" r="4" />
            <circle cx="1164" cy="474" r="4" />
          </g>
        </g>

        {/* Queue of workers */}
        <g fill="#0f172a">
          <g opacity="0.5">
            <circle cx="846" cy="586" r="20" />
            <path d="M800 698c6-58 22-102 46-102s38 46 44 102v10H800z" />
            <rect x="822" y="698" width="15" height="76" rx="4" />
            <rect x="850" y="698" width="15" height="76" rx="4" />
          </g>
          <g opacity="0.6">
            <circle cx="932" cy="558" r="22" />
            <path d="M880 690c8-68 26-116 52-116s44 52 50 116v12H880z" />
            <rect x="906" y="690" width="16" height="84" rx="4" />
            <rect x="938" y="690" width="16" height="84" rx="4" />
          </g>
          <g opacity="0.7">
            <circle cx="1024" cy="528" r="24" />
            <path d="M968 682c10-78 32-136 56-136s50 64 58 136v14h-114z" />
            <rect x="996" y="682" width="18" height="90" rx="4" />
            <rect x="1032" y="682" width="18" height="90" rx="4" />
          </g>
          <g opacity="0.86">
            <circle cx="1158" cy="508" r="26" />
            <path d="M1094 676c10-80 34-134 66-130 30 4 42 60 50 130v16h-116z" />
            <path d="M1198 548l40 38" stroke="#0f172a" strokeWidth="14" strokeLinecap="round" />
            <rect x="1188" y="574" width="22" height="14" rx="3" fill="#5eead4" />
            <rect x="1126" y="676" width="20" height="96" rx="4" />
            <rect x="1164" y="676" width="20" height="96" rx="4" />
          </g>
        </g>
      </svg>
      <div className="landing-punch-float absolute right-[7%] top-[18%] hidden rounded-lg border border-white/25 bg-white/95 px-3 py-2 shadow-xl sm:block">
        <p className="text-[10px] font-bold uppercase tracking-wide text-teal-700">IN</p>
        <p className="font-mono text-lg font-semibold text-slate-800">06:58</p>
        <p className="text-[11px] text-slate-500">Badge 4412</p>
      </div>
      <div className="landing-punch-float-slow absolute right-[24%] top-[38%] hidden rounded-lg border border-white/25 bg-white/95 px-3 py-2 shadow-xl md:block">
        <p className="text-[10px] font-bold uppercase tracking-wide text-rose-700">OUT</p>
        <p className="font-mono text-lg font-semibold text-slate-800">15:31</p>
        <p className="text-[11px] text-slate-500">Person 1008</p>
      </div>
      <div className="landing-punch-float absolute right-[3%] top-[56%] hidden rounded-lg border border-white/25 bg-white/95 px-3 py-2 shadow-xl lg:block">
        <p className="text-[10px] font-bold uppercase tracking-wide text-teal-700">IN</p>
        <p className="font-mono text-lg font-semibold text-slate-800">07:04</p>
        <p className="text-[11px] text-slate-500">NICE · LoggedIn</p>
      </div>
    </div>
  );
}

function PunchTape() {
  const loop = [...PUNCH_TAPE, ...PUNCH_TAPE];
  return (
    <div className="relative z-[1] overflow-hidden border-y border-white/10 bg-slate-950/55">
      <div className="landing-punch-tape flex items-center gap-8 px-6 py-2.5">
        {loop.map(([kind, time, who], i) => (
          <span key={`${who}-${i}`} className="flex shrink-0 items-center gap-2 font-mono text-xs text-slate-300">
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                kind === 'IN' ? 'bg-teal-400/20 text-teal-200' : 'bg-rose-400/20 text-rose-200'
              }`}
            >
              {kind}
            </span>
            <span className="text-white">{time}</span>
            <span className="text-slate-400">{who}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage({ onSignIn }) {
  return (
    <div className="landing-shell relative min-h-screen text-slate-100">
      <header className="tidesync-brand-strip sticky top-0 z-20 border-b border-cyan-300/50 shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6">
          <a href="#top" className="flex shrink-0 items-center rounded-lg bg-white/80 px-2 py-1 shadow-sm">
            <img
              src="/tidesync-logo.png?v=2"
              alt="TideSync — Time punches to OTL"
              className="h-10 w-auto max-w-[12rem] object-contain object-left sm:h-12"
            />
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-800 md:flex">
            <a href="#how" className="hover:text-teal-800">
              How it works
            </a>
            <a href="#prebuilts" className="hover:text-teal-800">
              Prebuilts
            </a>
            <a href="#after-load" className="hover:text-teal-800">
              After the load
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={WEBSITE_SIGNUP_URL}
              className="hidden rounded-lg px-3 py-2 text-sm font-medium text-slate-800 hover:text-teal-900 sm:inline"
            >
              Create account
            </a>
            <button
              type="button"
              onClick={onSignIn}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white shadow-md hover:bg-slate-800"
            >
              Sign in
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main id="top" className="relative z-[1]">
        <section className="relative overflow-hidden">
          <PunchScene />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0b1c2c]/85 via-[#0b1c2c]/55 to-transparent" />
          <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:py-24">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cyan-200">
                Oracle Time and Labor
              </p>
              <h1 className="mt-5 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Time punches from any clock or system. Loaded into OTL.
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
                TideSync is not a clock. It takes punches from a physical clock or another workforce
                system, pairs them, maps the worker, and posts clean time records to Oracle Time and
                Labor — before payroll.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={onSignIn}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-cyan-50"
                >
                  Open TideSync
                  <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href="#prebuilts"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-medium text-slate-200 hover:border-white/30"
                >
                  See NICE & Accutime prebuilts
                </a>
              </div>
              <ul className="mt-8 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
                {[
                  'Prebuilt for NICE and Accutime',
                  'Any clock or software that can export punches',
                  'Preview segments before Fusion',
                  'Hours, time cards, and delete after load',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-5 shadow-2xl shadow-cyan-950/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-cyan-300">Load path</p>
                <ol className="mt-4 space-y-3 text-sm">
                  {[
                    ['Clock or software export', 'NICE · Accutime · CSV · XLSX'],
                    ['TideSync normalize', 'Pair · badge map · exceptions'],
                    ['Oracle Time and Labor', 'timeRecordEventRequests'],
                  ].map(([title, sub], i) => (
                    <li
                      key={title}
                      className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/5 px-3 py-3"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cyan-500/20 text-xs font-bold text-cyan-200">
                        {i + 1}
                      </span>
                      <span>
                        <span className="block font-medium text-white">{title}</span>
                        <span className="text-slate-400">{sub}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-xs text-slate-400">
                  Same pipeline for every source. NICE and Accutime are the first prebuilts.
                </p>
              </div>
            </div>
          </div>
        </section>

        <PunchTape />

        <section id="how" className="border-t border-white/10 bg-slate-900/40">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">How TideSync loads OTL</h2>
            <p className="mt-3 max-w-2xl text-slate-400">
              One ingest path. Vendor formats are adapters — not separate products.
            </p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((step) => (
                <article key={step.n} className="rounded-2xl border border-white/10 bg-slate-950/60 p-5">
                  <p className="text-xs font-bold tracking-widest text-cyan-400">{step.n}</p>
                  <h3 className="mt-2 font-semibold text-white">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="prebuilts" className="border-t border-white/10">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">
              Prebuilt for NICE and Accutime
            </h2>
            <p className="mt-3 max-w-2xl text-slate-400">
              TideSync is the ingest layer into Oracle Time and Labor. NICE and Accutime ship ready.
              Other clocks or systems load through the same punch-to-OTL path.
            </p>
            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {PREBUILTS.map((card) => (
                <article key={card.name} className="flex flex-col rounded-2xl border border-white/10 bg-slate-900/50 p-6">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold text-white">{card.name}</h3>
                    <span className="rounded-full bg-teal-400/15 px-2.5 py-0.5 text-xs font-semibold text-teal-200">
                      {card.badge}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-cyan-200/90">{card.title}</p>
                  <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-300">
                    {card.points.map((p) => (
                      <li key={p} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="after-load" className="border-t border-white/10 bg-slate-900/40">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <h2 className="text-2xl font-semibold text-white sm:text-3xl">After the load, stay out of the Fusion menu</h2>
            <p className="mt-3 max-w-2xl text-slate-400">
              TideSync is the payroll console around the ingest — not only the file drop.
            </p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: BarChart3, title: 'Hours by time type', body: 'Regular, overtime, absence labels from Fusion time records.' },
                { icon: LayoutList, title: 'Time card list', body: 'See what landed for a person and date range.' },
                { icon: Trash2, title: 'Delete and reload', body: 'Preview matches, then remove a bad card before pay.' },
                { icon: FileSpreadsheet, title: 'Job logs', body: 'Logs, errors, and archives for every upload.' },
              ].map((item) => (
                <article key={item.title} className="rounded-2xl border border-white/10 bg-slate-950/50 p-5">
                  <item.icon className="h-5 w-5 text-cyan-400" />
                  <h3 className="mt-3 font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-400">{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-white/10">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-semibold text-white">Built for Fusion payroll teams</h2>
              <p className="mt-3 text-slate-400">
                Use TideSync when punches already exist in NICE, Accutime, a badge clock, or a
                homegrown export — and the painful part is getting them into Oracle Time and Labor
                without a one-off integration project.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2">
                  <Clock3 className="h-4 w-4 shrink-0 text-cyan-400" />
                  Not hardware. Not a shop-floor clock. The OTL ingest layer.
                </li>
                <li className="flex gap-2">
                  <Upload className="h-4 w-4 shrink-0 text-cyan-400" />
                  Replaces custom OIC/HDL punch loads for file-based sources.
                </li>
                <li className="flex gap-2">
                  <Link2 className="h-4 w-4 shrink-0 text-cyan-400" />
                  Saved Fusion connections per environment.
                </li>
                <li className="flex gap-2">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-cyan-400" />
                  Tool login plus Oracle credentials — preview before anything posts.
                </li>
              </ul>
            </div>
            <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/10 to-teal-500/5 p-8">
              <p className="text-sm font-semibold uppercase tracking-wide text-cyan-200">Ready when you are</p>
              <h3 className="mt-3 text-2xl font-semibold text-white">Load the next punch file into OTL</h3>
              <p className="mt-3 text-slate-300">
                Sign in with your SLP account. Add a Fusion connection. Load NICE or Accutime
                punches into OTL.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onSignIn}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:from-cyan-400 hover:to-teal-400"
                >
                  Sign in to TideSync
                  <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href={WEBSITE_SIGNUP_URL}
                  className="inline-flex items-center rounded-lg border border-white/15 px-4 py-2.5 text-sm font-medium text-white hover:border-white/30"
                >
                  Create an SLP account
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-[1] border-t border-white/10 px-4 py-8 text-center text-xs text-slate-500 sm:px-6">
        TideSync by SLP Microsystems — time punches into Oracle Time and Labor.
      </footer>
    </div>
  );
}
